import {useState} from 'react';
import {FeltDefs, useFeltIds} from '../cuteWidgets/felt';
import {ToyShell, type MapToyProps} from '../cuteWidgets/mapToys';
import {PoissonRain} from './PoissonRain';
import {RAIN_TONES} from './rainModel';

/** Whether it is raining, and whether the clouds are on their way out. */
type Weather = 'clear' | 'raining' | 'clearing';

/**
 * A little rain cloud for the roadmap. Pressing it makes it rain over the
 * whole page (see {@link PoissonRain}); pressing it again, the rain's close
 * button or Escape rolls the clouds away.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy, and the rain while it falls.
 * @throws Never.
 */
export function ToyRainCloud({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [weather, setWeather] = useState<Weather>('clear');

  return (
    <>
      <ToyShell
        isPreview={isPreview}
        label="Rain cloud"
        caption={
          weather === 'clear'
            ? 'a rain cloud, heavy with notes'
            : 'it is raining over the whole page; press again to stop'
        }
        onPoke={() => setWeather(weather === 'clear' ? 'raining' : 'clearing')}
      >
        <svg
          viewBox="0 0 100 74"
          width={size}
          height={size * 0.74}
          overflow="visible"
          aria-hidden
        >
          <defs>
            <FeltDefs ids={ids} />
          </defs>
          <g filter={`url(#${ids.fuzz})`}>
            <ellipse cx={50} cy={38} rx={40} ry={16} fill="#C9D3DE" />
            <circle cx={34} cy={30} r={16} fill="#E4EAF0" />
            <circle cx={56} cy={24} r={20} fill="#F4F7FA" />
            <circle cx={74} cy={34} r={13} fill="#E4EAF0" />
          </g>
          {RAIN_TONES.map((tone, index) => (
            <ellipse
              key={tone.id}
              cx={26 + index * 12}
              cy={60 + (index % 2) * 6}
              rx={2.4}
              ry={3.4}
              fill={tone.color}
            />
          ))}
        </svg>
      </ToyShell>
      {weather !== 'clear' && !isPreview ? (
        <PoissonRain
          isStopping={weather === 'clearing'}
          onClose={() => setWeather('clear')}
        />
      ) : null}
    </>
  );
}
